import { useEffect, useCallback, useRef } from 'react';

const UC_ROOT_SELECTOR = '#usercentrics-cmp-ui';
const UC_MAIN_DIALOG_SELECTOR = '#uc-main-dialog';
const UC_OPEN_EVENTS = new Set(['CMP_SHOWN', 'CMP_REQUEST_DISPLAY']);
const UC_CLOSE_EVENTS = new Set([
  'ACCEPT_ALL',
  'DENY_ALL',
  'SAVE',
  'CMP_CLOSED',
  'CMP_CLOSE',
  'CLOSE',
  'CLOSED',
]);

interface UsercentricsUIEvent extends CustomEvent {
  detail: {
    type: string;
    source: string;
    abTestVariant?: string;
  };
}

interface UseUsercentricsStyleInjectionOptions {
  /** CSS string to inject into the shadow DOM */
  customStyle?: string;
  /** Unique ID to prevent duplicate style injection */
  styleId: string;
  /**
   * Callback executed when shadow DOM is ready.
   * Use for DOM manipulation that can't be done with CSS (e.g., SVG fill colors).
   * Called each time the banner is shown.
   */
  onReady?: (shadowRoot: ShadowRoot) => void;
}

/**
 * Hook to inject custom CSS and execute JS in Usercentrics V3 shadow DOM.
 *
 * V3 lazy loads the banner on open and destroys on close, so we need to:
 * 1. Listen for UC_UI_CMP_EVENT with CMP_SHOWN type
 * 2. Wait for shadow DOM content to be ready using MutationObserver
 * 3. Inject styles once #uc-main-dialog exists
 * 4. Execute onReady callback for JS-based DOM manipulation
 */
export const useUsercentricsStyleInjection = ({
  customStyle,
  styleId,
  onReady,
}: UseUsercentricsStyleInjectionOptions): void => {
  // Use ref to avoid recreating callbacks when onReady changes
  const onReadyRef = useRef(onReady);
  const observerRef = useRef<MutationObserver | null>(null);
  onReadyRef.current = onReady;

  const disconnectObserver = useCallback((): void => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const processInjection = useCallback(
    (shadowRoot: ShadowRoot): boolean => {
      // Check if the main dialog exists (indicates shadow DOM is populated)
      const mainDialog = shadowRoot.querySelector(UC_MAIN_DIALOG_SELECTOR);
      if (!mainDialog) {
        return false;
      }

      // Inject styles if provided and not already injected
      if (customStyle) {
        const existingStyle = shadowRoot.querySelector(
          `style[data-style-id="${styleId}"]`
        );
        if (!existingStyle) {
          const styleElement = document.createElement('style');
          styleElement.setAttribute('data-style-id', styleId);
          styleElement.textContent = customStyle;

          try {
            shadowRoot.appendChild(styleElement);
          } catch (e) {
            console.error(
              `[Usercentrics ${styleId}] Failed to inject styles:`,
              e
            );
          }
        }
      }

      // Execute onReady callback for JS-based manipulation
      if (onReadyRef.current) {
        try {
          onReadyRef.current(shadowRoot);
        } catch (e) {
          console.error(
            `[Usercentrics ${styleId}] Error in onReady callback:`,
            e
          );
        }
      }

      return true;
    },
    [customStyle, styleId]
  );

  const observeCurrentShadowRoot = useCallback((): void => {
    const usercentricsRoot =
      document.querySelector<HTMLElement>(UC_ROOT_SELECTOR);

    if (!usercentricsRoot?.shadowRoot) {
      return;
    }

    const shadowRoot = usercentricsRoot.shadowRoot;

    disconnectObserver();
    processInjection(shadowRoot);

    const observer = new MutationObserver(() => {
      processInjection(shadowRoot);
    });

    observer.observe(shadowRoot, {
      childList: true,
      subtree: true,
    });

    observerRef.current = observer;
  }, [disconnectObserver, processInjection]);

  const handleCmpEvent = useCallback(
    (event: UsercentricsUIEvent): void => {
      if (UC_OPEN_EVENTS.has(event.detail.type)) {
        observeCurrentShadowRoot();
      }

      if (UC_CLOSE_EVENTS.has(event.detail.type)) {
        disconnectObserver();
      }
    },
    [disconnectObserver, observeCurrentShadowRoot]
  );

  useEffect(() => {
    const eventHandler = (event: Event): void => {
      handleCmpEvent(event as UsercentricsUIEvent);
    };
    const resizeHandler = (): void => {
      window.requestAnimationFrame(observeCurrentShadowRoot);
      window.setTimeout(observeCurrentShadowRoot, 100);
    };

    window.addEventListener('UC_UI_CMP_EVENT', eventHandler);
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('UC_UI_CMP_EVENT', eventHandler);
      window.removeEventListener('resize', resizeHandler);
      disconnectObserver();
    };
  }, [disconnectObserver, handleCmpEvent, observeCurrentShadowRoot]);
};
